import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastService } from 'ng-uikit-pro-standard';
import { CartSummary, DeliveryMode, PaypalPage, StoreLocation } from 'src/app/shared-core/models/cart-data.model';
import { CardPayment, CheckoutData, KlarnaPaymentMethodCategory, Payments, PaymentType, PaymentType as PaymentTypeEnum } from 'src/app/shared-core/models/checkout-data.model';
import { MessageTopicsEnum } from 'src/app/shared-core/models/component-message.model';
import { Klarna } from 'src/app/shared-core/typings/klarna.model';
import { CheckoutService } from 'src/app/shared-core/services/checkout.service';
import { ComponentMessagingService } from 'src/app/shared-core/services/component-messaging.service';
import { PaypalService } from 'src/app/shared-core/services/paypal.service';
import { NetworkToken } from '../../shared-core/models/checkout-data.model';
import { CreditCard } from 'src/app/shared-core/models/payer-auth.model';
import { CreditCardFormComponent } from 'src/app/shared/credit-card-form/credit-card-form.component';

@Component({
  selector: 'app-checkout-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
})
export class PaymentComponent implements OnInit {
  @ViewChild('creditCardFormComponent')
  public creditCardComponent: CreditCardFormComponent;
  
  tokenSelectionForm: FormGroup;

  get giftCardCoversOrderTotal(): boolean {
    return this.giftCardAmount >= this.totalOrderAmount;
  }

  get isSavingPaymentMethod(): boolean {
    return this.saveTokenFormControl.value;
  }

  selectedKlarnaPaymentMethodCategory: KlarnaPaymentMethodCategory;

  public giftCardAmount: number = null;
  public totalOrderAmount: number = 0;
  public saveTokenFormControl = new FormControl(false);

  PaymentTypeEnum = PaymentTypeEnum;
  submitted: boolean = false;
  cartSummary: CartSummary;
  payments: Payments;
  model: CheckoutData;
  isPaypalPaymentDone: boolean = false;
  selectedPaymentType: PaymentTypeEnum;
  selectedNetworkToken: string;
  paymentTypes: PaymentTypeEnum[];
  hasSavedCardsOnFile: boolean = false;
  savedPaymentMethodFeatureFlag = false;
  showAmexNotAllowedErrorMessage = false;
  isLoggedIn = false;
  klarnaState: { isAvailable: boolean; error?: string } = { isAvailable: false };
  storeLocations: StoreLocation[] = [];

  constructor(
    public checkoutService: CheckoutService,
    private payPalService: PaypalService,
    private messaging: ComponentMessagingService,
    private toaster: ToastService,
    private formBuilder: FormBuilder) { }

  public ngOnInit(): void {
    this.tokenSelectionForm = this.formBuilder.group({
      tokenSelectionControl: [null, Validators.required],
    });

    this.messaging.onMessage().subscribe((msg) => {
      if (msg.topic == MessageTopicsEnum.PaymentInfoRequest) {
        this.onSubmit();
      }
    });

    this.checkoutService.getStores().subscribe(stores => {
      this.storeLocations = stores;
    });

    this.checkoutService.CheckoutDataSubject.subscribe((checkoutData: CheckoutData) => {
      if (!checkoutData) {
        return;
      }

      this.model = checkoutData;
      this.payments = checkoutData.Payments;

      this.totalOrderAmount = checkoutData.CartSummary.GrandTotal;

      this.savedPaymentMethodFeatureFlag = checkoutData.FeatureFlagsSavedPaymentMethods;
      this.isLoggedIn = this.model?.CartOrder?.LoggedIn;

      // workaround for klarna sometimes not loading properly due to an issue on their end
      if (this.payments.KlarnaPayment.Session) {
        this.klarnaState.isAvailable = true;
      }

      if(this.savedPaymentMethodFeatureFlag ){
        this.hasSavedCardsOnFile = this.payments.NetworkTokens?.length > 0;
      }
      
      if(this.hasSavedCardsOnFile) {
        const defaultToken = this.payments.NetworkTokens.find(t => t.IsDefault);

        this.tokenSelectionForm.patchValue({
          tokenSelectionControl: (defaultToken ?? this.payments.NetworkTokens[0])?.TokenInstrumentIdentifierId ?? ""
        });
      }
      
      // Always use the payment type from the backend, since it might have applied some rules to override front end selection.
      this.selectedPaymentType = this.payments.PaymentType;

      if(this.hasSavedCardsOnFile){
        // assign default card as selected 
        this.onTokenSelected(this.payments.NetworkTokens[0]);
      }

      this.paymentTypes = checkoutData.Payments?.DisplayPaymentTypes?.filter(
        (n) => n != PaymentTypeEnum.GiftCard && n != PaymentTypeEnum.Quote
      );
      
      if (checkoutData.CartSummary.GrandTotal > 10000) {
        this.paymentTypes = this.paymentTypes.filter((n) => n != PaymentTypeEnum.PayPal);
      }

      this.isPaypalPaymentDone = !!this.payments.PaypalPayment?.PaypalToken;

      const klarnaCat = this.payments.KlarnaPayment.Session?.payment_method_categories && this.payments.KlarnaPayment.Session.payment_method_categories[0];
      this.onPaymentTypeChange(this.selectedPaymentType, klarnaCat, false);

      if (checkoutData.Payments.GiftCard) {
        this.giftCardAmount = checkoutData.Payments.GiftCard.AmountUsable;

        // If gift card covers the entire order, then we don't need to accept another payment method.
        if (this.giftCardCoversOrderTotal) {
          this.onPaymentTypeChange(PaymentType.GiftCard, null, true);
        }
      }
    });

    this.checkoutService.CartSummarySubject.subscribe((data) => {
      if (!data) return;
      this.cartSummary = data;
    });

    this.checkoutService.PaypalPaymentSubject.subscribe((data) => {
      if (!data) return;

      this.payments = data;
      this.selectedPaymentType = data.PaymentType;
      this.isPaypalPaymentDone = !!data.PaypalPayment?.PaypalToken;
    });

    this.payPalService.Page = PaypalPage.Checkout;
    this.payPalService.renderPaypalButton('paypal-cart');
  }

  async onSubmit() {
    try {
      this.submitted = true;
      let creditCard: CreditCard = null;
      if (this.selectedPaymentType === PaymentTypeEnum.CreditCard) {

        if (!this.creditCardComponent.isValid()) {
          this.messaging.sendMessage(MessageTopicsEnum.PaymentInfoResponse, null);
          return;
        }

        creditCard = this.creditCardComponent.getCreditCard();
      }

      if (this.selectedPaymentType == PaymentTypeEnum.Klarna) {
        if (!this.klarnaState.isAvailable) {
          this.messaging.sendMessage(MessageTopicsEnum.PaymentInfoResponse, Error('Klarna is not available'));
          return;
        }
        const resp = await this.klarnaAuthorize();

        if (resp.approved) {
          // we can place the order
          this.payments.KlarnaPayment.authorization_token = resp.authorization_token;
        } else {
          if (resp.show_form) {
            if (!resp.error) {
              // user cancelled, we do nothing
            } else {
              console.error('Klarna expects these fields, but they are not provided', resp.error);
              this.toaster.error(`Klarna needs data which we did not provide, it says: ${resp.error}`);
            }
          } else {
            this.klarnaState.isAvailable = false;
            this.klarnaState.error = 'Klarna is not available for this purchase.';
            this.toaster.error(this.klarnaState.error + ' Resetting payment selection.');
            this.onPaymentTypeChange(PaymentType.CreditCard);
          }
          this.messaging.sendMessage(MessageTopicsEnum.PaymentInfoResponse, null);
          return;
        }
      }

      this.payments.PaymentType = this.selectedPaymentType;

      if (this.payments.PaymentType === PaymentTypeEnum.CreditCard) {
        let cardPayment = new CardPayment();
        cardPayment.CardHolder = creditCard.nameOnAccount;
        cardPayment.Card = creditCard.accountNumber;
        cardPayment.ExpiryMonth = creditCard.expirationMonth;
        cardPayment.ExpiryYear = creditCard.expirationYear;
        cardPayment.CSC = creditCard.cardCode;
        this.payments.CardPayment = cardPayment;
      } else if (this.payments.PaymentType === PaymentTypeEnum.SavedCreditCard) {
        let cardPayment = new CardPayment();
        cardPayment.TokenId = this.selectedNetworkToken;
        this.payments.CardPayment = cardPayment;
      } else {
        this.payments.CardPayment = null;
      }

      if (this.selectedPaymentType === PaymentTypeEnum.Lease) {
        this.payments.PaypalPayment = null;
        this.isPaypalPaymentDone = false;
      }

      this.checkoutService.updatePayment(this.payments).subscribe(
          (data) => {
            this.payments = {
              ...this.payments,
              ...{PaymentValid: data.PaymentValid, DisplayMessage: data.DisplayMessage},
            };
            this.submitted = false;
            this.isPaypalPaymentDone = !!data.PaypalPayment?.PaypalToken;
            if (this.payments?.DisplayMessage?.length > 0) {
              this.checkoutService.showErrorPanel(this.payments?.DisplayMessage);
              this.messaging.sendMessage(MessageTopicsEnum.PaymentInfoResponse, null);
            } else {
              //no errors, so continue to complete purchase
              this.messaging.sendMessage(MessageTopicsEnum.PaymentInfoResponse, this.payments);
            }
          },
          (error) => {
            this.messaging.sendMessage(MessageTopicsEnum.PaymentInfoResponse, error);
          }
      );
    }
    catch(err) {
      console.error('An unknown error occurred after pressing check out.', err);
      this.toaster.error('An unknown error has occurred. Please refresh your page and try again.');
      this.messaging.sendMessage(MessageTopicsEnum.PaymentInfoResponse, null);
    }
  }

  async klarnaAuthorize(): Promise<Klarna.KlarnaResponse> {
    const billing = this.model.CartOrder.Billing;
    const shipping = this.model.CartOrder;
    const pickupLocationCode = this.model.ShippingDetails?.PickupLocationCode;
    
    const request: Klarna.AuthorizeRequest = {
      billing_address: {
        city: billing.City,
        country: billing.CountryCode,
        email: this.model.CartOrder.Email,
        family_name: billing.LastName,
        given_name: billing.FirstName,
        phone: this.model.CartOrder.Phone,
        postal_code: billing.PostalCode,
        region: billing.StateProvince,
        street_address: billing.Address,
        street_address2: billing.Address2,
        title: '',
      },
    };

    const matchingStore = this.storeLocations.find(x => x.LocationCode == pickupLocationCode);

    if (matchingStore == null) {
      request.shipping_address = {
        city: shipping.Shipping.City,
        country: shipping.Shipping.CountryCode,
        email: this.model.CartOrder.Email,
        given_name: shipping.Shipping.FirstName,
        family_name: shipping.Shipping.LastName,
        phone: this.model.CartOrder.Phone,
        postal_code: shipping.Shipping.PostalCode,
        region: shipping.Shipping.StateProvince,
        street_address: shipping.Shipping.Address,
        street_address2: shipping.Shipping.Address2,
        title: '',
      };
    } else {
      const attachmentBody = {
        other_delivery_address: [
          {
            shipping_method: 'store pick-up',
            shipping_type: 'normal',
            first_name: this.model.CartOrder.FirstName,
            last_name: this.model.CartOrder.LastName,
            street_address: matchingStore.Address,
            postal_code: matchingStore.PostalCode,
            city: matchingStore.City,
            country: 'CA',
          },
        ],
      } as Klarna.OtherAddressAttachment;
      request.attachment = {
        content_type: 'application/vnd.klarna.internal.emd-v2+json',
        body: JSON.stringify(attachmentBody),
      };
    }
    
    return new Promise<Klarna.KlarnaResponse>((resolve, reject) => {
      window.Klarna.Payments.authorize(
        {
          payment_method_category: this.selectedKlarnaPaymentMethodCategory.identifier,
        },
        request,
        (res: Klarna.KlarnaResponse) => {
          resolve(res);
        }
      );
    });
  }

  onTokenSelected(token: NetworkToken){
    this.selectedNetworkToken = token.TokenInstrumentIdentifierId;
    this.payments.CardPayment.TokenId = this.selectedNetworkToken;
  }

  onPaymentTypeChange(
    paymentType: PaymentTypeEnum,
    category: KlarnaPaymentMethodCategory = null,
    notify: boolean = true
  ) {
    this.selectedPaymentType = paymentType;
    this.selectedKlarnaPaymentMethodCategory = category;

    if (notify) {
      this.checkoutService.PaymentTypeSubject.next(this.selectedPaymentType);
    }
    this.payments.DisplayMessage = null;

    this.initPaymentWidget(paymentType);
  }

  private initPaymentWidget(paymentType: PaymentTypeEnum) {
    if (paymentType == PaymentTypeEnum.Klarna) {
      const klarnaPayments = window.Klarna.Payments;
      klarnaPayments.init({
        client_token: this.payments.KlarnaPayment.Session.client_token,
      });
      var categoryIdentifier = this.selectedKlarnaPaymentMethodCategory.identifier;
      setTimeout(() => {
        klarnaPayments.load(
          {
            container: '#klarna-payments-container',
            payment_method_category: categoryIdentifier,
          },
          (res: Klarna.KlarnaResponse) => {
            this.klarnaState.isAvailable = res.show_form;
            if (res.error || !this.klarnaState.isAvailable) {
              console.error('Klarna load() error:' + res.error);
              this.klarnaState.error = 'Klarna is not available at this moment.';
              this.toaster.error(this.klarnaState.error + ' Resetting payment selection.');
              this.onPaymentTypeChange(PaymentType.CreditCard);
            }
          }
        );
      });
    }
  }

    onValidCardDetected(event: { cardType: string }) {
        if(!this.model.CanUseAmexForWebSales){
            this.showAmexNotAllowedErrorMessage = event.cardType === 'american-express';
        }
    
    }
}
